import { Button } from '@neo/index'
import { ButtonWithLoader } from '@neo/primitives/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import { useKeyboardShortcuts } from '@shared/hooks/use-keyboard-shortcuts'
import type { ApiResponse } from '@shared/types'
import { useMutation } from '@tanstack/react-query'
import type { Edge, Node } from '@xyflow/react'
import axios, { type AxiosError } from 'axios'
import { useCommandRegister } from 'cmdk-engine/react'
import { ArrowRightLeft, MoreVertical, Pause, Pencil, Play, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'router-v6'
import { queryClient } from 'v2/configs/rq-setup'
import { DuplicateFlowDialog } from 'v2/pages/settings/flows/duplicate-flow-dialog'
import { ButtonWithPermission } from 'v2/shared/components/button-with-permission'
import { DefaultFlowWarning } from 'v2/shared/components/default-flow-warning'
import { useDefaultFlowCheck } from 'v2/shared/hooks/use-default-flow-check'
import { useLivekitEnabled } from 'v2/shared/hooks/use-livekit-enabled'
import { usePermissions } from 'v2/shared/hooks/use-permissions'
import { settingsService } from 'v2/shared/services/settings-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { getFlowBuilderCommands } from 'v2/shared/utils/commands/flow-builder-commands'
import { toKeyboardShortcuts, withDisplayShortcuts } from 'v2/shared/utils/commands/types'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import type { FlowTypes, PhloType } from 'v2/types'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { agentLogger } from 'v2/utils/logger'
import { errorToast, successToast } from 'v2/utils/toasts'
import { useAgentSettingsFormValues } from '../../hooks/use-agent-settings-form-values'
import { useFlowId } from '../../hooks/use-flow-id'
import { usePublishFlow } from '../../hooks/use-publish-flow'
import { useTypedReactFlow } from '../../hooks/use-react-flow'
import { useStartNodeType } from '../../hooks/use-start-node-type'
import { useVersionView } from '../../hooks/use-version-view'
import { useFlowStateMutation } from '../../queries'
import { reactFlowContentSchema } from '../../schemas/react-flow-schemas'
import { useFlowStore } from '../../store'
import {
  AgentState,
  type FlowData,
  type GenericEdge,
  type GenericNode,
  type NodeDataBase,
  type SystemPrompt,
} from '../../types'
import { checkUnsavedChanges, isLegacyReadOnly } from '../../utils'
import { hasAiAgentV2WithMeta, hasCallChannelAiAgentV2 } from '../../utils/call-channel-detection'
import { validateFlowContentForUi } from '../../utils/flow-validation'
import { getImportedAgentSettings } from '../../utils/imported-agent-settings'
import {
  cleanAgentSettingsForExport,
  cleanConversationGoalsForExport,
  cleanNodesForExport,
} from '../../utils/node-cleanup'
import { FlowVersionHistory } from './flow-version-history'
import { PlaygroundButton } from './playground-button'
import { useAppNavigate } from 'v2/shared/hooks/use-app-navigate'

// import { useUnsavedChanges } from '../../hooks/use-unsaved-changes'

interface FlowActionButtonsProps {
  flowData?: FlowData
  name?: string
  pasteContent: (
    nodes: Node<NodeDataBase<unknown>>[],
    edges: Edge[],
    sourceDescription: string,
    metadata?: { importedSystemPrompt?: SystemPrompt | null },
  ) => void
}

interface ActionMenuProps {
  canImport: boolean
  uuid?: string
}

const MIGRATION_PROMPT =
  'Migrate this agent flow to the new merged AI conversation pattern. Collapse the AI conversation and action nodes into a single AI agent node with embedded actions.'

export const FlowActionButtons = ({ flowData, name, pasteContent }: FlowActionButtonsProps) => {
  const { mutate: updateFlowState, isPending: isStateChanging } = useFlowStateMutation()
  const isEditing = useFlowStore((state) => state.isEditing)
  const setIsEditing = useFlowStore((state) => state.setIsEditing)

  // While viewing a historical version, the lifecycle actions (Edit / Pause / Publish /
  // Test) act on the live flow, so they're disabled (not hidden) — see the fieldset in
  // the render. The menu + version dropdown stay enabled for navigating back. (SER-5591)
  const { isViewingHistorical } = useVersionView()

  // TEMPORARY WORKAROUND (v3 edit-lock; remove when the task compiler is re-enabled):
  // v3 task-runtime flows (any ai_agent_v2 node with a compiled `_meta`) are frozen
  // from editing while the compiler is disabled — an edit can't recompile. Gates the
  // Save / Save-as-draft / Publish / Edit buttons below (disabled={... || hasCompiledMeta}).
  const hasCompiledMeta = !!flowData && hasAiAgentV2WithMeta(flowData.nodes)

  // Registered commands/shortcuts must go inert in lockstep with the on-page lifecycle
  // buttons — otherwise Edit/Publish stay invokable from the palette or ⌘E/⌘P while the
  // button itself is disabled.
  const isLifecycleActionsDisabled = isViewingHistorical || hasCompiledMeta

  const { hasPermission } = usePermissions()
  const canEditAgent = hasPermission(RbacModules.aiAgent, RbacPermissionTypes.edit)

  const builderCommands = useMemo(
    () =>
      withDisplayShortcuts(
        getFlowBuilderCommands({
          isEditing,
          isDisabled: isLifecycleActionsDisabled,
          canEdit: canEditAgent,
        }),
      ),
    [isEditing, isLifecycleActionsDisabled, canEditAgent],
  )
  useCommandRegister(builderCommands, [builderCommands])
  useKeyboardShortcuts(useMemo(() => toKeyboardShortcuts(builderCommands), [builderCommands]))

  // Use the custom hook for reactive unsaved changes detection
  // const { hasUnsavedChanges } = useUnsavedChanges({ flowData }) // TODO: Uncomment this when we have a better way to track unsaved changes

  const { publishFlow, isSavingDraft, isPublishingFlow } = usePublishFlow()
  const { getNodes, getEdges, updateNodeData } = useTypedReactFlow()
  const setCurrentFlowType = useFlowStore((state) => state.setCurrentFlowType)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const flowType = useStartNodeType()
  // Holds the flow id when the duplicate dialog is open (empty string = closed).
  // Only surfaced for legacy flows so customers can migrate to an editable Agent.
  const [duplicateFlowId, setDuplicateFlowId] = useState('')
  // Read-only legacy flows (migrated, still owned by the old console) only get
  // the Duplicate affordance; duplicates of legacy flows are editable and get
  // the regular action buttons.
  const isReadOnlyLegacyFlow = isLegacyReadOnly(flowData?.phlo)
  // Narrow once so the Duplicate handler gets a non-undefined string.
  const legacyFlowId = isReadOnlyLegacyFlow ? flowData?.phlo.id : undefined
  const navigate = useAppNavigate()
  const location = useLocation()
  const { isLivekitEnabled } = useLivekitEnabled()
  const { isNew } = useFlowId()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally capture only on mount
  const arrivedViaMigration = useMemo(() => !!location.state?.initialPrompt, [])

  const needsLivekitMigration =
    !!flowData &&
    isLivekitEnabled &&
    !isNew &&
    !arrivedViaMigration &&
    !flowData.global_meta?.livekit_ready &&
    hasCallChannelAiAgentV2(flowData.nodes)

  const [migrateConfirmOpen, setMigrateConfirmOpen] = useState(false)
  const migrateMutation = useMutation({
    mutationFn: async () => {
      const flowId = flowData?.phlo?.id ?? ''
      const baseName = `${name} v2`
      let lastErr: unknown
      for (let i = 0; i < 5; i++) {
        const tryName = i === 0 ? baseName : `${baseName} (${i + 1})`
        try {
          return await settingsService.duplicateFlow(flowId, tryName)
        } catch (err) {
          lastErr = err
          if (axios.isAxiosError(err) && err.response?.status === 400 && i < 4) continue
          throw err
        }
      }
      throw lastErr
    },
    onSuccess: (response) => {
      const newId = (response?.data as unknown as { phlo_id?: string })?.phlo_id
      if (!newId) {
        errorToast({
          title: 'Error',
          description: 'Migration failed: could not determine the new agent id.',
        })
        return
      }
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().flowsBase })
      setMigrateConfirmOpen(false)
      navigate(`/agent/builder/${newId}`, {
        state: { initialPrompt: MIGRATION_PROMPT },
      })
    },
    onError: (error) => {
      const errorText = getErrorText(error as AxiosError<ApiResponse<{ message: string }>>)
      errorToast({
        title: 'Error',
        description:
          (Array.isArray(errorText) ? errorText.join(', ') : errorText) ||
          'Failed to duplicate agent',
      })
    },
  })

  const handleMigrateDialogOpenChange = (open: boolean) => {
    if (migrateMutation.isPending) return
    setMigrateConfirmOpen(open)
  }

  useEffect(() => {
    const open = () => setMigrateConfirmOpen(true)
    window.addEventListener('open-legacy-migrate-dialog', open)
    return () => window.removeEventListener('open-legacy-migrate-dialog', open)
  }, [])

  const {
    isDefaultFlow,
    defaultForChannels,
    isLoading: isCheckingDefaultFlow,
  } = useDefaultFlowCheck(flowData?.phlo?.id)

  // canImport is true when in draft mode or editing — but never while viewing a
  // historical version (`?version`), where Import would mutate the read-only canvas.
  // ActionMenu sits outside the disabled lifecycle <fieldset>, so gate it here. (SER-5591)
  const canImport = !isViewingHistorical && (flowData?.phlo.state === AgentState.DRAFT || isEditing)

  const handleStateChange = (action: 'pause' | 'resume') => {
    if (!flowData?.phlo?.id) return
    updateFlowState(
      { id: flowData.phlo.id, action },
      {
        onSuccess: () => {
          successToast({
            title: 'Success',
            description: `AI Agent ${action === 'pause' ? 'paused' : 'resumed'} successfully`,
          })
        },
        onError: (err) => {
          const errorText = getErrorText(err as AxiosError<ApiResponse<{ message: string }>>)
          errorToast({
            title: 'Error',
            description: errorText || `Failed to ${action} AI agent`,
          })
        },
      },
    )
  }

  const handleEdit = () => {
    if (!flowData) return

    const { selectedNodeID } = useFlowStore.getState()
    setIsEditing(true, selectedNodeID)
  }

  const handleImportMenuItemClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    if (file.type !== 'application/json') {
      errorToast({ title: 'Error', description: 'Invalid file type. Please select a JSON file.' })
      return
    }

    try {
      const fileContent = await file.text()
      const parsedJson = JSON.parse(fileContent)

      const validationResult = reactFlowContentSchema.safeParse(parsedJson)

      if (!validationResult.success) {
        errorToast({
          title: 'Import Error',
          description: 'The selected JSON file has an invalid structure.',
        })
        return
      }

      const {
        nodes: importedNodes,
        edges: importedEdges,
        systemPrompt,
        sttGuidance,
        agentName,
        agentPersona,
        conversationGoals,
        agentSettings,
      } = validationResult.data

      if ('sttGuidance' in validationResult.data) {
        const { setSttGuidance } = useFlowStore.getState()
        setSttGuidance(sttGuidance ?? null)
      }

      if ('agentName' in validationResult.data) {
        const { setAgentName } = useFlowStore.getState()
        setAgentName(agentName ?? null)
      }

      if ('agentPersona' in validationResult.data) {
        const { setAgentPersona } = useFlowStore.getState()
        setAgentPersona(agentPersona ?? null)
      }

      const importedAgentSettings = getImportedAgentSettings({
        agentSettings,
        ...('conversationGoals' in validationResult.data ? { conversationGoals } : {}),
      })

      if (importedAgentSettings) {
        const { setAgentSettings } = useFlowStore.getState()
        setAgentSettings(importedAgentSettings)
      }

      pasteContent(
        importedNodes as Node<NodeDataBase<unknown>>[],
        importedEdges as Edge[],
        'file',
        {
          ...('systemPrompt' in validationResult.data
            ? { importedSystemPrompt: systemPrompt ?? null }
            : {}),
        },
      )

      if (event.target && fileInputRef.current) {
        fileInputRef.current.value = '' // Reset file input
      }
    } catch (_err) {
      errorToast({
        title: 'Import Error',
        description: 'Could not process the selected JSON file.',
      })
      if (event.target && fileInputRef.current) {
        fileInputRef.current.value = '' // Reset file input
      }
    }
  }

  const { getSettingsFormValues } = useAgentSettingsFormValues()

  const getSaveDisabledReason = () => {
    if (!name) return 'Valid Agent name is required'
    // TEMPORARY WORKAROUND (v3 edit-lock; remove when the task compiler is re-enabled).
    if (hasCompiledMeta) return 'Editing is disabled for this flow.'
    return undefined
  }

  const handlePublishFlow = async (isDraft = false) => {
    if (!flowData) return

    // Read form values directly without updating the store
    // to avoid triggering GenericForm's form.reset() via defaultValues change
    const formValues = getSettingsFormValues()

    checkUnsavedChanges(true, async () => {
      try {
        await publishFlow(flowData, { isDraft, name, agentSettingsOverride: formValues })
      } catch (error) {
        // Error handling is already done in publishFlow hook
        agentLogger.debug(`Failed to ${isDraft ? 'save flow as draft' : 'publish flow'}:`, error)
      }
    })
  }

  const handleDownloadJson = () => {
    const currentNodes = getNodes() as GenericNode[]
    const currentEdges = getEdges() as GenericEdge[]
    const shouldValidate = false
    if (shouldValidate) {
      const validationResult = validateFlowContentForUi(
        currentNodes,
        currentEdges,
        flowType as FlowTypes,
        false,
        'download',
      )

      if (!validationResult.isValid) {
        if (validationResult.issuesToUpdate) {
          validationResult.issuesToUpdate.forEach((issue) => {
            const issueNodeID = issue.params?.data.id
            if (issueNodeID) {
              updateNodeData(issueNodeID, (o) => {
                const existingMeta = o.data.meta || { name: '' }
                return {
                  meta: {
                    ...existingMeta,
                    issues: [issue],
                  },
                }
              })
            }
          })
        }
        if (validationResult.error) {
          errorToast(validationResult.error)
        }
        return
      }
    }

    // Get system prompt, stt guidance from store; use live form values for agent settings
    const { systemPrompt, sttGuidance, agentName, agentPersona } = useFlowStore.getState()
    const agentSettings = getSettingsFormValues()

    // Remove position, zIndex and measured fields from nodes before exporting
    const cleanedNodes = cleanNodesForExport(currentNodes)

    const cleanedAgentSettings = agentSettings ? cleanAgentSettingsForExport(agentSettings) : null
    const conversationGoals = cleanConversationGoalsForExport(agentSettings?.conversation_goals)

    const flowConfiguration = {
      name: name || null,
      flowId: flowData?.phlo?.id || null,
      nodes: cleanedNodes,
      edges: currentEdges,
      systemPrompt: systemPrompt || null,
      sttGuidance: sttGuidance || null,
      agentName: agentName || null,
      agentPersona: agentPersona || null,
      conversationGoals,
      agentSettings: cleanedAgentSettings,
    }
    let jsonString = ''
    try {
      jsonString = JSON.stringify(flowConfiguration, null, 2)
    } catch (_err) {
      // Although this would never happen, as the flow configuration is serializable,
      // Adding it just in case.
      errorToast({
        title: 'Export Error',
        description: 'Could not export the flow configuration.',
      })
      return
    }
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${name || 'flow'}-config.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    successToast({
      title: 'Success',
      description: 'Agent configuration downloaded successfully.',
    })
  }

  const ActionMenu = ({ canImport }: ActionMenuProps) => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="iconSm" data-testid="button-flow-options">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleDownloadJson} data-testid="dropdown-item-download-json">
            <span>Download</span>
          </DropdownMenuItem>
          {legacyFlowId && (
            <DropdownMenuItem
              onClick={() => setDuplicateFlowId(legacyFlowId)}
              data-testid="dropdown-item-duplicate-flow"
            >
              <span>Duplicate</span>
            </DropdownMenuItem>
          )}
          {canImport && (
            <DropdownMenuItem
              onClick={handleImportMenuItemClick}
              data-testid="dropdown-item-import-json"
            >
              <span>Import from file</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  const MigrateButton = () => (
    <Button
      variant="default"
      size="sm"
      typography="action"
      onClick={() => setMigrateConfirmOpen(true)}
      data-testid="button-migrate-flow"
    >
      <ArrowRightLeft className="h-4 w-4 mr-2" />
      Upgrade to v2
    </Button>
  )

  const renderButtons = () => {
    // Read-only legacy flows have no action buttons
    if (isReadOnlyLegacyFlow) return null

    const handleCancel = () => {
      setIsEditing(false)
      setCurrentFlowType(flowData?.phlo.phlo_type as PhloType)
    }

    if (needsLivekitMigration) {
      return <MigrateButton />
    }

    // Draft mode - Show Save and Publish buttons
    if (flowData?.phlo.state === AgentState.DRAFT) {
      return (
        <>
          <Button
            variant="outline"
            size="sm"
            typography="action"
            type="button"
            disabled={!name || isSavingDraft || isPublishingFlow || hasCompiledMeta}
            disabledReason={getSaveDisabledReason()}
            onClick={() => handlePublishFlow(true)}
            data-testid="button-save-flow"
          >
            {isSavingDraft ? 'Saving as draft...' : 'Save as draft'}
          </Button>
          <Button
            variant="default"
            size="sm"
            typography="action"
            type="button"
            disabled={!name || isSavingDraft || isPublishingFlow || hasCompiledMeta}
            disabledReason={getSaveDisabledReason()}
            onClick={() => handlePublishFlow()}
            data-testid="button-publish-flow"
          >
            {isPublishingFlow ? 'Publishing...' : 'Publish'}
          </Button>
        </>
      )
    }

    // Paused mode - Show Resume button
    if (flowData?.phlo.state === AgentState.PAUSED) {
      return isEditing ? (
        <>
          <Button
            variant="outline"
            size="sm"
            typography="action"
            onClick={handleCancel}
            data-testid="button-cancel-edit"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <ButtonWithPermission
            module={RbacModules.aiAgent}
            permissionType={RbacPermissionTypes.edit}
            variant="default"
            size="sm"
            typography="action"
            onClick={() => handlePublishFlow()}
            disabled={isPublishingFlow || hasCompiledMeta}
            disabledReason={getSaveDisabledReason()}
            data-testid="button-publish-flow"
          >
            <Play className="h-4 w-4 mr-2" />
            {isPublishingFlow ? 'Publishing...' : 'Publish'}
          </ButtonWithPermission>
        </>
      ) : (
        <>
          <Button
            variant="outline"
            size="sm"
            typography="action"
            onClick={handleEdit}
            disabled={hasCompiledMeta}
            disabledReason={getSaveDisabledReason()}
            data-testid="button-edit-flow"
          >
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <ButtonWithPermission
            module={RbacModules.aiAgent}
            permissionType={RbacPermissionTypes.edit}
            variant="default"
            size="sm"
            typography="action"
            onClick={() => handleStateChange('resume')}
            disabled={isStateChanging}
            data-testid="button-resume-flow"
          >
            <Play className="h-4 w-4 mr-2" />
            Publish
          </ButtonWithPermission>
        </>
      )
    }

    // Active mode - Show Edit/Cancel and Pause/Publish buttons
    return isEditing ? (
      <>
        <Button
          variant="outline"
          size="sm"
          typography="action"
          onClick={handleCancel}
          data-testid="button-cancel-edit"
        >
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
        <ButtonWithPermission
          module={RbacModules.aiAgent}
          permissionType={RbacPermissionTypes.edit}
          variant="default"
          size="sm"
          typography="action"
          onClick={() => handlePublishFlow()}
          disabled={isPublishingFlow || hasCompiledMeta}
          disabledReason={getSaveDisabledReason()}
          data-testid="button-publish-flow"
        >
          {isPublishingFlow ? 'Publishing...' : 'Publish'}
        </ButtonWithPermission>
      </>
    ) : (
      <>
        <DefaultFlowWarning
          isDefaultFlow={isDefaultFlow}
          defaultForChannels={defaultForChannels}
          isPauseDisabled={isStateChanging}
          onPauseConfirm={() => handleStateChange('pause')}
          trigger={
            <ButtonWithPermission
              module={RbacModules.aiAgent}
              permissionType={RbacPermissionTypes.edit}
              variant="outline"
              size="sm"
              typography="action"
              disabled={isStateChanging || isCheckingDefaultFlow}
              data-testid="button-pause-flow"
            >
              <Pause className="h-4 w-4 mr-2" />
              Pause
            </ButtonWithPermission>
          }
        />
        <ButtonWithPermission
          module={RbacModules.aiAgent}
          permissionType={RbacPermissionTypes.edit}
          variant="default"
          size="sm"
          typography="action"
          onClick={handleEdit}
          disabled={hasCompiledMeta}
          disabledReason={getSaveDisabledReason()}
          data-testid="button-edit-flow"
        >
          <Pencil className="h-4 w-4 mr-2" />
          Edit
        </ButtonWithPermission>
      </>
    )
  }

  return (
    <>
      <ActionMenu canImport={canImport} uuid={flowData?.phlo.id} />
      <FlowVersionHistory flowData={flowData} isNew={isNew} />
      {/* While viewing a historical version the lifecycle actions (Test / Edit / Pause /
          Publish / Save) stay visible but disabled — they act on the live flow. A native
          <fieldset disabled> disables every contained button in one place. (SER-5591) */}
      <fieldset
        disabled={isViewingHistorical}
        className="m-0 flex items-center gap-2 border-0 p-0 disabled:opacity-50"
      >
        <PlaygroundButton flowData={flowData} />
        {renderButtons()}
      </fieldset>
      <input
        type="file"
        accept=".json"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileSelected}
        data-testid="input-file-flow-json"
      />
      {duplicateFlowId && (
        <DuplicateFlowDialog
          state={duplicateFlowId}
          setState={setDuplicateFlowId}
          name={flowData?.phlo.name}
        />
      )}

      <Dialog open={migrateConfirmOpen} onOpenChange={handleMigrateDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate and upgrade this agent?</DialogTitle>
            <DialogDescription>
              We'll create a copy named "{name} v2" and open it with the AI agent ready to upgrade
              it. Your original agent stays untouched.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              typography="action"
              onClick={() => setMigrateConfirmOpen(false)}
              disabled={migrateMutation.isPending}
              data-testid="button-migrate-flow-cancel"
            >
              Cancel
            </Button>
            <ButtonWithLoader
              typography="action"
              loading={migrateMutation.isPending}
              disabled={migrateMutation.isPending}
              onClick={() => migrateMutation.mutate()}
              data-testid="button-migrate-flow-confirm"
            >
              {migrateMutation.isPending ? 'Upgrading...' : 'Duplicate & upgrade'}
            </ButtonWithLoader>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
